import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime, BACKEND } from "@/lib/api";
import { usePoll, PageHeader, Panel, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const KINDS = [["off_air", "Off air"], ["back_on_air", "Back on air"], ["source_down", "Source down"], ["switch", "Source switch"], ["failback", "Failback"], ["limit_reached", "Connection limit"], ["blocklist_refused", "Blocklist refused"], ["acceptance_report", "Acceptance report"], ["failure_digest", "Weekly failure digest"], ["black_picture", "Black picture"], ["output_check", "Output check failed"], ["output_check_ok", "Output check recovered"]];
const lbl = "text-[10px] font-semibold uppercase tracking-wider text-slate-400";

function AlertLog() {
  const { data } = usePoll("/admin/alerts/log?limit=50", 5000);
  return (
    <Panel title="Delivery log" testid="alert-log">
      <div className="max-h-80 overflow-y-auto">
        {(data || []).map((l) => (
          <div key={l.id} className="flex items-center gap-3 py-1.5 border-b border-slate-800/60 text-xs font-mono">
            <span className="text-slate-500 w-32 shrink-0">{fmtTime(l.ts, true)}</span>
            <Pill tone={l.ok ? "green" : "red"}>{l.ok ? `ok ${l.status || ""}` : "failed"}</Pill>
            <Pill>{l.kind}</Pill>{l.test && <Pill tone="blue">test</Pill>}
            <span className="text-slate-300">{l.target}</span>
            <span className="text-slate-500 truncate" title={l.error || l.url}>{l.error || l.url}</span>
          </div>
        ))}
        {data && data.length === 0 && <p className="text-sm text-slate-500">No alerts sent yet.</p>}
      </div>
    </Panel>
  );
}

function Sink() {
  const { data } = usePoll("/admin/webhook-sink", 5000);
  return (
    <Panel title="Built-in test receiver" testid="webhook-sink">
      <p className="text-xs text-slate-400 mb-2">Add this URL as a webhook to see exactly what downstream servers receive:</p>
      <code className="block text-xs text-cyan-300 mb-3 break-all" data-testid="webhook-sink-url">{BACKEND}/api/webhook-sink</code>
      <div className="max-h-64 overflow-y-auto space-y-2">
        {(data || []).slice(0, 10).map((x, i) => (
          <pre key={i} className="text-[11px] bg-[#0B111B] border border-slate-800 rounded p-2 text-slate-300 overflow-x-auto">{fmtTime(x.ts)} {JSON.stringify(x.body)}</pre>
        ))}
        {data && data.length === 0 && <p className="text-sm text-slate-500">Nothing received yet.</p>}
      </div>
    </Panel>
  );
}

export default function Alerts() {
  const [s, setS] = useState(null);
  const [hooks, setHooks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => { setS(data); setHooks((data.webhook_urls || []).join("\n")); }); }, []);
  if (!s) return null;
  const kinds = s.alert_kinds || [];
  const toggle = (k) => setS({ ...s, alert_kinds: kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k] });
  const body = () => ({ alerts_enabled: s.alerts_enabled, alert_kinds: kinds, webhook_urls: hooks.split(/\s+/).filter(Boolean), telegram_bot_token: s.telegram_bot_token || "", telegram_chat_id: s.telegram_chat_id || "" });
  const save = async () => {
    try { const { data } = await api.put("/admin/settings", body()); setS(data); toast.success("Alert settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", body());
      const { data } = await api.post("/admin/alerts/test");
      if (!data.sent) toast.warning("No alert targets configured");
      else data.results.forEach((r) => (r.ok ? toast.success(`${r.target}: delivered`) : toast.error(`${r.target}: ${r.error || r.status}`)));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader title="Alerts" sub="Tell downstream servers and your team when a channel goes off air and comes back."
        right={<><Button data-testid="alerts-test-btn" variant="outline" className="border-slate-700" disabled={busy} onClick={test}><Send size={14} /> Send test</Button>
          <Button data-testid="alerts-save-btn" onClick={save} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">Save</Button></>} />
      <div className="grid xl:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Panel title="Delivery">
            <label className="flex items-center gap-2 text-sm text-slate-200 mb-4"><Switch data-testid="alerts-enabled-switch" checked={!!s.alerts_enabled} onCheckedChange={(v) => setS({ ...s, alerts_enabled: v })} /> Alerts enabled</label>
            <div className={lbl}>Send on</div>
            <div className="grid grid-cols-2 gap-1.5 mt-2">
              {KINDS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-300"><Checkbox data-testid={`alert-kind-${k}`} checked={kinds.includes(k)} onCheckedChange={() => toggle(k)} />{l}</label>
              ))}
            </div>
          </Panel>
          <Panel title="Webhooks">
            <div className={lbl}>Global webhook URLs (one per line)</div>
            <Textarea data-testid="alerts-webhooks-input" rows={3} className="mt-1 bg-[#0B111B] border-slate-700 font-mono text-xs" value={hooks} onChange={(e) => setHooks(e.target.value)} placeholder="https://hooks.example.com/origin" />
            <p className="text-[11px] text-slate-500 mt-2">JSON POST: <code className="text-slate-300">{"{event, channel, message, level, ts, origin, on_air}"}</code>. Each authorized server can also have its own webhook (edit the server); it only receives alerts for channels granted to it.</p>
          </Panel>
          <Panel title="Telegram">
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1"><span className={lbl}>Bot token (from @BotFather)</span>
                <Input data-testid="telegram-token-input" className="bg-[#0B111B] border-slate-700 font-mono text-xs" value={s.telegram_bot_token || ""} onChange={(e) => setS({ ...s, telegram_bot_token: e.target.value })} placeholder="123456:ABC…" /></label>
              <label className="space-y-1"><span className={lbl}>Chat ID</span>
                <Input data-testid="telegram-chat-input" className="bg-[#0B111B] border-slate-700 font-mono text-xs" value={s.telegram_chat_id || ""} onChange={(e) => setS({ ...s, telegram_chat_id: e.target.value })} placeholder="-1001234567890" /></label>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Add the bot to your group or channel, then use that chat's ID. The token is stored on the origin and shown masked.</p>
          </Panel>
        </div>
        <div className="space-y-4"><AlertLog /><Sink /></div>
      </div>
    </div>
  );
}
